import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type { KnowledgeCategory, KnowledgeQueryFilters } from '@shared/knowledge';
import { ChatPanel } from './ChatPanel';

const CATEGORIES: { value: KnowledgeCategory; label: string }[] = [
  { value: 'process', label: 'Processes' },
  { value: 'decision', label: 'Decisions' },
  { value: 'stakeholder', label: 'Stakeholders' },
  { value: 'tacit', label: 'Tacit Knowledge' },
  { value: 'context', label: 'Context' },
];

export function KnowledgePage() {
  const navigate = useNavigate();
  const [selectedCategories, setSelectedCategories] = useState<KnowledgeCategory[]>([]);
  const [showFilters, setShowFilters] = useState(false);

  const toggleCategory = (category: KnowledgeCategory) => {
    setSelectedCategories((prev) =>
      prev.includes(category)
        ? prev.filter((c) => c !== category)
        : [...prev, category]
    );
  };

  const filters: KnowledgeQueryFilters | undefined =
    selectedCategories.length > 0 ? { categories: selectedCategories } : undefined;

  return (
    <main className="flex h-screen flex-col bg-background text-foreground">
      {/* Header */}
      <header className="shrink-0 border-b border-border bg-card px-4 py-3 shadow-sm sm:px-6">
        <div className="mx-auto flex max-w-5xl items-center justify-between">
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={() => navigate('/')}
              className="flex h-8 w-8 items-center justify-center rounded-lg text-muted-foreground transition hover:bg-muted hover:text-foreground"
              aria-label="Go back to home"
            >
              <svg
                className="h-5 w-5"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={2}
                aria-hidden="true"
              >
                <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
              </svg>
            </button>
            <div>
              <h1 className="text-base font-semibold text-foreground sm:text-lg">
                Knowledge Base
              </h1>
              <p className="hidden text-xs text-muted-foreground sm:block">
                Search extracted interview knowledge
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setShowFilters(!showFilters)}
              className={`flex items-center gap-1.5 rounded-lg border px-3 py-1.5 text-xs font-medium transition ${
                showFilters || selectedCategories.length > 0
                  ? 'border-kaiva-coral/40 bg-kaiva-coral/10 text-kaiva-coral'
                  : 'border-border bg-card text-muted-foreground hover:text-foreground'
              }`}
              aria-label="Toggle filters"
              aria-expanded={showFilters}
            >
              <svg
                className="h-3.5 w-3.5"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={2}
                aria-hidden="true"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M12 3c2.755 0 5.455.232 8.083.678.533.09.917.556.917 1.096v1.044a2.25 2.25 0 01-.659 1.591l-5.432 5.432a2.25 2.25 0 00-.659 1.591v2.927a2.25 2.25 0 01-1.244 2.013L9.75 21v-6.568a2.25 2.25 0 00-.659-1.591L3.659 7.409A2.25 2.25 0 013 5.818V4.774c0-.54.384-1.006.917-1.096A48.32 48.32 0 0112 3z"
                />
              </svg>
              Filters
              {selectedCategories.length > 0 && (
                <span className="flex h-4 w-4 items-center justify-center rounded-full bg-kaiva-coral text-[9px] font-bold text-white">
                  {selectedCategories.length}
                </span>
              )}
            </button>
          </div>
        </div>

        {/* Filter panel */}
        {showFilters && (
          <div className="mx-auto mt-3 max-w-5xl border-t border-border pt-3">
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
                Categories:
              </span>
              {CATEGORIES.map(({ value, label }) => {
                const isActive = selectedCategories.includes(value);
                return (
                  <button
                    key={value}
                    type="button"
                    onClick={() => toggleCategory(value)}
                    className={`rounded-full border px-3 py-1 text-xs font-medium transition ${
                      isActive
                        ? 'border-kaiva-coral bg-kaiva-coral/10 text-kaiva-coral'
                        : 'border-border bg-card text-muted-foreground hover:border-kaiva-coral/30 hover:text-foreground'
                    }`}
                    aria-pressed={isActive}
                  >
                    {label}
                  </button>
                );
              })}
              {selectedCategories.length > 0 && (
                <button
                  type="button"
                  onClick={() => setSelectedCategories([])}
                  className="text-xs text-muted-foreground underline transition hover:text-foreground"
                >
                  Clear all
                </button>
              )}
            </div>
          </div>
        )}
      </header>

      {/* Chat area */}
      <div className="mx-auto flex w-full max-w-5xl flex-1 flex-col overflow-hidden">
        <ChatPanel filters={filters} />
      </div>
    </main>
  );
}
